import React, { useId, useMemo } from "react";
import { maxSeriesValue } from "../../utils/followersDeskHelpers";
import { HourlyHeatStrip, TopViewersRanked } from "./AudienceChartViz";

const CHART_W = 280;
const CHART_H = 96;
const PAD = 8;

const buildSeriesPath = (values, max, innerW, innerH) => {
  if (max <= 0 || !values.length) return "";
  return values
    .map((value, index) => {
      const x = PAD + (index / Math.max(values.length - 1, 1)) * innerW;
      const y = PAD + innerH - (value / max) * innerH;
      return `${index === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
};

/** Dual line chart for recorded follow vs unfollow events over 7 days. */
export const FollowDailyTrendChart = ({ items, ariaLabel }) => {
  const followGradId = useId();

  const followValues = items.map((item) => item.follows);
  const unfollowValues = items.map((item) => item.unfollows);
  const max = maxSeriesValue([...followValues, ...unfollowValues]);

  const paths = useMemo(() => {
    if (max <= 0) return null;
    const innerW = CHART_W - PAD * 2;
    const innerH = CHART_H - PAD * 2;
    const followLine = buildSeriesPath(followValues, max, innerW, innerH);
    const unfollowLine = buildSeriesPath(unfollowValues, max, innerW, innerH);
    const lastX = PAD + innerW;
    const baseY = CHART_H - PAD;
    const followArea = followLine
      ? `${followLine} L ${lastX.toFixed(1)} ${baseY} L ${PAD} ${baseY} Z`
      : "";
    return { followLine, unfollowLine, followArea, innerW, innerH };
  }, [followValues, unfollowValues, max]);

  if (max <= 0 || !paths) return null;

  const followPoints = followValues.map((value, index) => {
    const x = PAD + (index / Math.max(items.length - 1, 1)) * paths.innerW;
    const y = PAD + paths.innerH - (value / max) * paths.innerH;
    return { x, y, value, key: `f-${items[index].key}` };
  });

  const unfollowPoints = unfollowValues.map((value, index) => {
    const x = PAD + (index / Math.max(items.length - 1, 1)) * paths.innerW;
    const y = PAD + paths.innerH - (value / max) * paths.innerH;
    return { x, y, value, key: `u-${items[index].key}` };
  });

  return (
    <div className="bd-audience-viz bd-audience-viz-trend bd-follower-viz-trend">
      <svg
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        className="bd-audience-trend-svg"
        role="img"
        aria-label={ariaLabel}
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id={followGradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#64b5f6" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#1e88e5" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((ratio) => (
          <line
            key={ratio}
            x1={PAD}
            y1={PAD + (CHART_H - PAD * 2) * ratio}
            x2={CHART_W - PAD}
            y2={PAD + (CHART_H - PAD * 2) * ratio}
            className="bd-audience-trend-grid"
          />
        ))}
        {paths.followArea ? <path d={paths.followArea} fill={`url(#${followGradId})`} /> : null}
        {paths.followLine ? (
          <path d={paths.followLine} className="bd-follower-trend-line-follow" fill="none" />
        ) : null}
        {paths.unfollowLine ? (
          <path d={paths.unfollowLine} className="bd-follower-trend-line-unfollow" fill="none" />
        ) : null}
        {followPoints.map((p) =>
          p.value > 0 ? (
            <circle key={p.key} cx={p.x} cy={p.y} r={3} className="bd-follower-trend-dot-follow">
              <title>{`${p.value} follows`}</title>
            </circle>
          ) : null
        )}
        {unfollowPoints.map((p) =>
          p.value > 0 ? (
            <circle key={p.key} cx={p.x} cy={p.y} r={3} className="bd-follower-trend-dot-unfollow">
              <title>{`${p.value} unfollows`}</title>
            </circle>
          ) : null
        )}
      </svg>
      <div className="bd-audience-trend-labels">
        {items.map((item) => (
          <span key={item.key}>{item.label}</span>
        ))}
      </div>
      <div className="bd-follower-trend-legend">
        <span>
          <i className="bd-audience-dot bd-audience-dot-unique" />
          Follows
        </span>
        <span>
          <i className="bd-audience-dot bd-follower-dot-unfollow" />
          Unfollows
        </span>
      </div>
    </div>
  );
};

/** Donut for recorded follow vs unfollow events; center shows current follower count. */
export const FollowMixDonut = ({ follows, unfollows, currentFollowers, compact = false }) => {
  const uid = useId();
  const total = follows + unfollows;
  if (total <= 0) return null;

  const size = compact ? 88 : 112;
  const stroke = compact ? 9 : 11;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const followShare = follows / total;
  const unfollowShare = unfollows / total;
  const followLen = c * followShare;
  const unfollowLen = c * unfollowShare;
  const followPct = Math.round(followShare * 100);
  const unfollowPct = 100 - followPct;

  return (
    <div className={`bd-audience-viz bd-audience-viz-donut${compact ? " bd-audience-viz-compact" : ""}`}>
      <div
        className="bd-audience-donut-wrap"
        style={{ width: size, height: size }}
      >
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={stroke}
          />
          {follows > 0 ? (
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={`url(#${uid}-follow)`}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={`${followLen} ${c - followLen}`}
              strokeDashoffset={0}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          ) : null}
          {unfollows > 0 ? (
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={`url(#${uid}-unfollow)`}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={`${unfollowLen} ${c - unfollowLen}`}
              strokeDashoffset={-followLen}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          ) : null}
          <defs>
            <linearGradient id={`${uid}-follow`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#64b5f6" />
              <stop offset="100%" stopColor="#1e88e5" />
            </linearGradient>
            <linearGradient id={`${uid}-unfollow`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#ef9a9a" />
              <stop offset="100%" stopColor="#ef5350" />
            </linearGradient>
          </defs>
        </svg>
        <div className="bd-audience-donut-center">
          <strong className="tabular-nums">{currentFollowers}</strong>
          <span>now</span>
        </div>
      </div>
      <ul className="bd-audience-donut-legend">
        <li>
          <i className="bd-audience-dot bd-audience-dot-unique" />
          Follow events
          <strong className="tabular-nums">{follows}</strong>
          <em>{followPct}%</em>
        </li>
        <li>
          <i className="bd-audience-dot bd-follower-dot-unfollow" />
          Unfollow events
          <strong className="tabular-nums">{unfollows}</strong>
          <em>{unfollowPct}%</em>
        </li>
      </ul>
    </div>
  );
};

export { HourlyHeatStrip, TopViewersRanked as TopFollowersRanked };
